import musicOgg from '../../assets/audio/main-menu/contagion-menu.ogg?url';
import musicMp3 from '../../assets/audio/main-menu/contagion-menu.mp3?url';
import hoverUrl from '../../assets/audio/main-menu/ui-hover.wav?url';
import confirmUrl from '../../assets/audio/main-menu/ui-confirm.wav?url';
import startUrl from '../../assets/audio/main-menu/start-heavy-mechanism.wav?url';
import { createMenuCueEngine, type MenuCueName } from './menu-cues';

export type MenuAudioState = 'idle' | 'playing' | 'muted' | 'unavailable';

export interface MenuAudioOptions {
  doc: Document;
  onStateChange?: (state: MenuAudioState) => void;
}

export interface MenuAudio {
  unlock(): void;
  toggle(): void;
  hover(): void;
  confirm(): void;
  start(): void;
  finishForBattle(): Promise<void>;
  setVisible(visible: boolean): void;
  dispose(): void;
  getState(): MenuAudioState;
}

const MUTE_KEY = 'rts-reforged.menu-muted';
const MUSIC_VOLUME = 0.7;
const BATTLE_FADE_MS = 350;

type IdleCapableWindow = Window & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/** Menu media stays independent of the match's gesture-owned AudioContext. */
export function createMenuAudio({ doc, onStateChange }: MenuAudioOptions): MenuAudio {
  let muted = false;
  try {
    muted = doc.defaultView?.localStorage.getItem(MUTE_KEY) === 'true';
  } catch {
    // Private/embedded browsing may deny storage; the current session still works.
  }
  let state: MenuAudioState = muted ? 'muted' : 'idle';
  let unlocked = false;
  let visible = true;
  let pageHidden = false;
  let disposed = false;
  let generation = 0;
  let pending: number | null = null;
  let pendingAutomatic = false;
  let music: HTMLAudioElement | null = null;
  let lastHover = -Infinity;
  let prefetchFrame: number | null = null;
  let prefetchIdle: number | null = null;
  let paintObserver: PerformanceObserver | null = null;
  let cuesPrepared = false;
  let metadataPrepared = false;
  let fade: {
    promise: Promise<void>;
    resolve: () => void;
    timer: ReturnType<typeof setTimeout> | undefined;
  } | null = null;
  const cues = new Map<string, HTMLAudioElement>();
  const cueByUrl: Record<string, MenuCueName> = {
    [hoverUrl]: 'hover',
    [confirmUrl]: 'confirm',
    [startUrl]: 'start',
  };
  const cueEngine = createMenuCueEngine({
    doc,
    cues: {
      hover: { url: hoverUrl, volume: 0.5 },
      confirm: { url: confirmUrl, volume: 0.65 },
      start: { url: startUrl, volume: 0.8 },
    },
  });
  const pageAllowsAudio = () =>
    !disposed && !pageHidden && doc.visibilityState !== 'hidden' && !muted;
  const allowed = () => pageAllowsAudio() && visible;
  const cuesAllowed = () => allowed() && (unlocked || state === 'playing');
  const publish = (next: MenuAudioState) => {
    if (state === next || disposed) return;
    state = next;
    onStateChange?.(state);
  };
  const musicError = () => {
    if (!allowed()) return;
    generation++;
    pending = null;
    music?.pause();
    publish('unavailable');
  };
  const musicStarted = () => {
    // WebKit can deliver media events after a pending play() was cancelled.
    // Hidden menus must be silent even before that promise settles.
    if (allowed()) return;
    if (music) music.muted = true;
    music?.pause();
  };
  const playbackFailed = (error: unknown) => {
    pending = null;
    const name = typeof error === 'object' && error !== null && 'name' in error ? error.name : '';
    publish(name === 'NotAllowedError' || name === 'AbortError' ? 'idle' : 'unavailable');
    if (name === 'NotAllowedError' || name === 'AbortError') scheduleMusicPrefetch();
  };

  function cancelMusicPrefetch() {
    const view = doc.defaultView as IdleCapableWindow | null;
    if (prefetchFrame !== null)
      (view?.cancelAnimationFrame ?? globalThis.cancelAnimationFrame)?.(prefetchFrame);
    if (prefetchIdle !== null) view?.cancelIdleCallback?.(prefetchIdle);
    paintObserver?.disconnect();
    paintObserver = null;
    prefetchFrame = null;
    prefetchIdle = null;
  }

  function scheduleMusicPrefetch() {
    if (prefetchFrame !== null || prefetchIdle !== null || paintObserver || !allowed()) return;
    const view = doc.defaultView as IdleCapableWindow | null;
    const requestFrame = view?.requestAnimationFrame
      ? (callback: FrameRequestCallback) => view.requestAnimationFrame(callback)
      : globalThis.requestAnimationFrame;
    if (!requestFrame) return;
    const prepare = () => {
      prefetchIdle = null;
      if (!allowed()) return;
      if (!cuesPrepared) {
        cuesPrepared = true;
        cueEngine.preload();
      }
      if (metadataPrepared || !music || music.error || !music.paused || pending !== null) return;
      // Metadata asks the browser to prepare the stream prefix without taking
      // the bandwidth and decode cost of a full-track cache warm-up.
      metadataPrepared = true;
      music.preload = 'metadata';
      music.load();
    };
    const afterPaint = () => {
      paintObserver?.disconnect();
      paintObserver = null;
      prefetchFrame = requestFrame(() => {
        prefetchFrame = null;
        if (!allowed()) return;
        if (view?.requestIdleCallback) {
          prefetchIdle = view.requestIdleCallback(prepare);
          return;
        }
        prepare();
      });
    };
    const supportsPaint =
      typeof PerformanceObserver !== 'undefined' &&
      PerformanceObserver.supportedEntryTypes.includes('paint');
    if (supportsPaint) {
      try {
        const observer = new PerformanceObserver((entries: PerformanceObserverEntryList) => {
          if (entries.getEntries().some((entry) => entry.name === 'first-contentful-paint'))
            afterPaint();
        });
        paintObserver = observer;
        observer.observe({ type: 'paint', buffered: true });
        return;
      } catch {
        paintObserver?.disconnect();
        paintObserver = null;
      }
    }
    // Older engines may not expose paint entries; two frames are the closest
    // available fallback without delaying a real interaction.
    prefetchFrame = requestFrame(() => {
      prefetchFrame = null;
      afterPaint();
    });
  }

  function startMusic(explicitRetry = false) {
    if (!allowed() || fade) return;
    if (explicitRetry) cancelMusicPrefetch();
    try {
      if (music?.error) {
        if (!explicitRetry) {
          publish('unavailable');
          return;
        }
        // load() clears a sticky MediaError and restarts source selection. Invalidate
        // older play promises before it aborts them; only a new gesture retries I/O.
        generation++;
        pending = null;
        music.load();
      }
      if (pending !== null) {
        if (!explicitRetry || !pendingAutomatic) return;
        // A slow autoplay request must not consume the first valid gesture.
        // Cancel it before retrying so a late rejection cannot replace this result.
        generation++;
        pending = null;
        music?.pause();
      }
      if (!music) {
        music = doc.createElement('audio');
        music.preload = 'none';
        music.loop = true;
        music.volume = MUSIC_VOLUME;
        music.src = music.canPlayType('audio/ogg; codecs="opus"') ? musicOgg : musicMp3;
        music.addEventListener('error', musicError);
        music.addEventListener('play', musicStarted);
        music.addEventListener('playing', musicStarted);
      }
      music.muted = false;
      music.volume = MUSIC_VOLUME;
      if (!music.paused) return;
      const token = generation;
      const current = music;
      pending = token;
      pendingAutomatic = !explicitRetry;
      // Try autoplay first. Gesture retries must call play synchronously to retain activation.
      void Promise.resolve(current.play()).then(
        () => {
          if (token !== generation || !allowed()) {
            if (!allowed()) current.pause();
            return;
          }
          pending = null;
          publish('playing');
        },
        (error: unknown) => {
          if (token !== generation || !allowed()) return;
          playbackFailed(error);
        },
      );
    } catch (error: unknown) {
      playbackFailed(error);
    }
  }

  function stop(finishStartCue = false) {
    if (fade) {
      clearTimeout(fade.timer);
      const resolve = fade.resolve;
      fade = null;
      resolve();
    }
    cancelMusicPrefetch();
    generation++;
    pending = null;
    if (music) {
      // Mute is a second synchronous barrier against a stale media play event.
      // The same element is unmuted only by the next permitted menu playback.
      music.muted = true;
      music.pause();
    }
    for (const [url, cue] of cues) {
      if (!finishStartCue || url !== startUrl) cue.pause();
    }
    cueEngine.stop(finishStartCue);
    publish(muted ? 'muted' : 'idle');
  }

  function finishForBattle(): Promise<void> {
    if (fade) return fade.promise;
    if (!music || music.paused || !allowed()) {
      visible = false;
      stop(true);
      return Promise.resolve();
    }
    cancelMusicPrefetch();
    const current = music;
    const started = performance.now();
    const initialVolume = current.volume;
    let resolve!: () => void;
    const promise = new Promise<void>((done) => {
      resolve = done;
    });
    const transition = {
      promise,
      resolve,
      timer: undefined as ReturnType<typeof setTimeout> | undefined,
    };
    fade = transition;
    const tick = () => {
      if (fade !== transition) return;
      const fraction = Math.min(1, (performance.now() - started) / BATTLE_FADE_MS);
      current.volume = initialVolume * (1 - fraction);
      if (fraction >= 1 || !allowed()) {
        visible = false;
        stop(true);
      } else transition.timer = setTimeout(tick, 16);
    };
    tick();
    return promise;
  }

  function playCue(url: string, volume: number) {
    if (!cuesAllowed()) return;
    try {
      // One action owns the UI feedback; its click replaces a hover tail.
      for (const [otherUrl, other] of cues) {
        if (otherUrl !== url) other.pause();
      }
      const name = cueByUrl[url];
      if (name && cueEngine.available()) {
        // Do not turn a cold decode into feedback long after this interaction.
        // A supported engine either starts now or drops this one cue; subsequent
        // gestures use the predecoded buffer.
        cueEngine.play(name);
        return;
      }
      let cue = cues.get(url);
      if (!cue) {
        cue = doc.createElement('audio');
        cue.preload = 'auto';
        cue.src = url;
        cue.volume = volume;
        cues.set(url, cue);
      }
      cue.currentTime = 0;
      const current = cue;
      const token = generation;
      void Promise.resolve(current.play()).then(
        () => {
          const mayFinish = url === startUrl ? pageAllowsAudio() : allowed();
          if (token !== generation && !mayFinish) current.pause();
        },
        () => {},
      );
    } catch {
      // Optional UI feedback never prevents a menu action.
    }
  }

  const visibilityChanged = () => {
    if (allowed()) {
      startMusic();
      scheduleMusicPrefetch();
    } else stop();
  };
  const dispose = () => {
    if (disposed) return;
    stop();
    disposed = true;
    cancelMusicPrefetch();
    music?.removeEventListener('error', musicError);
    music?.removeEventListener('play', musicStarted);
    music?.removeEventListener('playing', musicStarted);
    doc.removeEventListener('visibilitychange', visibilityChanged);
    doc.defaultView?.removeEventListener('pagehide', pageHide);
    doc.defaultView?.removeEventListener('pageshow', pageShow);
    for (const media of [music, ...cues.values()]) {
      if (!media) continue;
      media.removeAttribute('src');
      media.load();
    }
    cues.clear();
    cueEngine.dispose();
    music = null;
  };
  const pageHide = (event: PageTransitionEvent) => {
    if (!event.persisted) dispose();
    else {
      pageHidden = true;
      stop();
    }
  };
  const pageShow = () => {
    pageHidden = false;
    startMusic();
    scheduleMusicPrefetch();
  };
  doc.addEventListener('visibilitychange', visibilityChanged);
  doc.defaultView?.addEventListener('pagehide', pageHide);
  doc.defaultView?.addEventListener('pageshow', pageShow);
  // Sound is enabled unless the player chose mute. A browser may require unlock()
  // from the first menu interaction; a blocked play promise does not change that choice.
  if (!muted) {
    startMusic();
    scheduleMusicPrefetch();
  }

  return {
    unlock() {
      if (disposed) return;
      unlocked = true;
      cueEngine.preload();
      cueEngine.unlock();
      startMusic(true);
    },
    toggle() {
      if (disposed) return;
      muted = !muted;
      unlocked = true;
      try {
        doc.defaultView?.localStorage.setItem(MUTE_KEY, String(muted));
      } catch {
        // Keep an in-memory preference when storage is unavailable.
      }
      stop();
      if (!muted) {
        cueEngine.preload();
        cueEngine.unlock();
        startMusic(true);
      }
    },
    hover() {
      const now = doc.defaultView?.performance.now() ?? 0;
      if (!cuesAllowed() || now - lastHover < 100) return;
      lastHover = now;
      playCue(hoverUrl, 0.5);
    },
    confirm() {
      playCue(confirmUrl, 0.65);
    },
    start() {
      playCue(startUrl, 0.8);
    },
    finishForBattle,
    setVisible(value) {
      if (disposed) return;
      if (value && fade) stop();
      if (value && value === visible) {
        startMusic();
        return;
      }
      visible = value;
      if (allowed()) {
        startMusic();
        scheduleMusicPrefetch();
      }
      // The launch mechanism finishes across the menu-to-battle transition.
      // Tab hiding, mute, pagehide and dispose still stop every sound at once.
      else stop(true);
    },
    dispose,
    getState: () => state,
  };
}
